import api from "../utils/axios";
import {
  useQuery,
  useMutation,
  useQueryClient,
  keepPreviousData,
} from "@tanstack/react-query";
import { showToast } from "../components/ui/toast";

const AML_KEY = ["aml"];

// GET dashboard list + summary counts.
// params: { status, riskLevel, search, page, pageSize } — all optional.
// Returns the full response ({ counts, data, pagination }) since the page
// needs the counts and rows together.
export const useAmlQuery = (params = {}) => {
  return useQuery({
    queryKey: [...AML_KEY, params],
    queryFn: async () => {
      const res = await api.get("/admin/aml", { params });
      return res.data || {};
    },
    // Hold the previous page on screen while the next one loads. Without this
    // the page falls back to its skeleton on every filter/search/page change,
    // which unmounts the table — and with it the search box the user is typing
    // into. (`keepPreviousData: true` is the v4 spelling and is a no-op in v5.)
    placeholderData: keepPreviousData,
  });
};

// GET single AML record by ID (for the detail modal).
export const useAmlByIdQuery = (id, options = {}) => {
  return useQuery({
    queryKey: [...AML_KEY, id],
    queryFn: async () => {
      const res = await api.get(`/admin/aml/${id}`);
      return res.data?.data || null;
    },
    enabled: !!id,
    ...options,
  });
};

// Fetch a signed download URL for a deal's compliance report, on demand.
// Called from a button click (not on render), so it's a plain async function
// rather than a hook. Returns the signed URL string, or throws on failure.
export const fetchAmlReportUrl = async (id) => {
  const res = await api.get(`/admin/aml/${id}/downloadReport`);
  const url = res.data?.data?.downloadUrl;
  if (!url) throw new Error("No download URL returned");
  return url;
};

// Replace a deal's compliance report PDF. The file is written over the same S3
// key, so links already shared keep working and point at the new document.
export const useReplaceAmlReportMutation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, file }) => {
      const formData = new FormData();
      formData.append("report", file);
      const res = await api.put(`/admin/aml/${id}/report`, formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      return res.data?.data || {};
    },
    onSuccess: () => {
      showToast("success", "Compliance report replaced");
    },
    onError: (err) => {
      showToast(
        "error",
        err?.response?.data?.message || "Failed to replace compliance report",
      );
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: AML_KEY });
    },
  });
};
